import React, { createContext, useContext } from 'react'

import { VersionComparison, useVersionComparison } from './useVersionComparison'

const compareContext = createContext<VersionComparison | undefined>(undefined)

interface CompareProviderProps {
  /** The page being compared, or undefined before it is known. */
  pageUUID?: string
  children: React.ReactNode
}

/**
 * Runs the comparison once, for everything that shows it.
 *
 * The canvas and the sidebar are in different parts of the tree but need the
 * same answer, and the comparison loads both versions in a worker of its own -
 * so it is held here rather than run in either of them.
 *
 * What is selected is deliberately not here: the canvas already has a selection
 * of its own, and a second one would mean a change picked from the list and the
 * same thing clicked on the canvas lit up differently.
 */
export function CompareProvider({ pageUUID, children }: CompareProviderProps) {
  const comparison = useVersionComparison(pageUUID)

  return (
    <compareContext.Provider value={comparison}>
      {children}
    </compareContext.Provider>
  )
}

/**
 * The running comparison, or undefined outside a canvas view.
 *
 * Optional so that consumers shared with surfaces that never compare - the
 * version list in a Preview, say - keep working without one.
 */
export function useCompare(): VersionComparison | undefined {
  return useContext(compareContext)
}
